import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import { useMappedGetter } from '@/store'
import { useReactiveFeatureFlag } from '@/utils/useFeatureFlags'

import type { CoreBrand, CoreLocation } from '../types'
import { useCoreStore } from '../useCoreStore'

/**
 * Temporary composable to allow direct access to the core store manipulation in the core store
 * while we roll out the store migration. This composable makes no assumptions about whether or not
 * the activeBrand is defined or any similar assumptions, this is direct store access.
 *
 * This composable should be cleaned up as part of the hermes.usePiniaStore feature flag removal, with
 * usages replaced with direct useCoreStore() integration
 */
export const useTempCoreStoreAccess = () => {
  const { enabled: usePiniaStore } = useReactiveFeatureFlag(
    'hermes.usePiniaStore'
  )

  const {
    activeBrand: piniaActiveBrand,
    activeBrandId: piniaActiveBrandId,
    activeLocationId: piniaActiveLocationId,
  } = storeToRefs(useCoreStore())

  const vuexActiveBrandId = useMappedGetter<string | undefined>(
    'core/brand/active/id'
  )

  const vuexActiveBrand = useMappedGetter<CoreBrand | undefined>(
    'core/brand/active/*'
  )

  const vuexActiveLocationId = useMappedGetter<string | undefined>(
    'core/location/active/id'
  )

  const activeBrandId = computed(() =>
    usePiniaStore.value ? piniaActiveBrandId.value : vuexActiveBrandId.value
  )
  const activeLocationId = computed(() =>
    usePiniaStore.value
      ? piniaActiveLocationId.value
      : vuexActiveLocationId.value
  )

  const activeBrand = computed(() =>
    usePiniaStore.value ? piniaActiveBrand.value : vuexActiveBrand.value
  )

  const vuexActiveBrandLocations = useMappedGetter<CoreLocation[] | undefined>(
    'core/brand/active/locations'
  )

  const piniaActiveBrandLocations = computed(
    () => piniaActiveBrand.value?.locations ?? []
  )

  const activeBrandLocations = computed(
    () =>
      (usePiniaStore.value
        ? piniaActiveBrandLocations.value
        : vuexActiveBrandLocations.value) ?? []
  )

  return {
    activeBrand,
    activeBrandId,
    activeBrandLocations,
    activeLocationId,
  }
}
